import _sortBy from 'lodash.sortby';
import BaseActionAbstract from 'cio/app-models/action-abstracts/base';
import { set, setProperties } from '@ember/object';
import { computed } from '@ember/object';
import { task } from 'ember-concurrency';
import { isNone } from '@ember/utils';
import { MAX_RANDOM_COHORT_ALLOCATION } from 'cio/utils/visual-workflow-builder';
import {
  formatRandomCohortPathValue,
  edgesEndingInExitCount,
  retrieveOutgoingEdges,
} from 'cio/utils/visual-workflow-builder/branching';

const EDITABLE_PROPERTIES: string[] = ['name'];
const DIRTY_DEPENDENCIES = `action.{${EDITABLE_PROPERTIES.join(',')}}`;

class CohortAbstract {
  value!: number;
  edge: any;
  branchReconnectPoint: any;

  parentAbstract!: RandomCohortSplitAbstract;

  _originalValue!: number;

  from: any;
  to: any;
  type: any;
  index!: number;

  isNew = false;

  constructor(
    value: any,
    edge: any,
    branchReconnectPoint: any,
    parentAbstract: RandomCohortSplitAbstract,
    isNew = false,
  ) {
    setProperties(this, {
      edge,
      branchReconnectPoint,
      from: edge.from,
      to: edge.to,
      type: edge.type,
      index: edge.index,
      value,
      _originalValue: value,
      parentAbstract,
      isNew,
    });
  }

  @computed('value', 'parentAbstract.{isDistributedEvenly,evenDistributionValue}')
  get normalizedValue(): number {
    if (this.parentAbstract.isDistributedEvenly) {
      if (this.value === 0) {
        return 0;
      }

      return this.parentAbstract.evenDistributionValue;
    }

    return this.value;
  }

  @computed('normalizedValue')
  get formattedValue(): string {
    return formatRandomCohortPathValue(this.normalizedValue);
  }

  @computed('_originalValue', 'value')
  get isDirty(): boolean {
    return this._originalValue !== this.value;
  }

  @computed('to', 'branchReconnectPoint', 'isNew')
  get isPathEmpty(): boolean {
    if (this.isNew) {
      return true;
    }

    if (!this.branchReconnectPoint) {
      return isNone(this.to);
    }

    return this.to === this.branchReconnectPoint;
  }
}

export default class RandomCohortSplitAbstract extends BaseActionAbstract {
  type = 'random_cohort_split';

  action!: any;
  actionType!: any;

  preconditions = [];
  multiConditions!: any;

  originalEdges: any;
  cohortAbstracts!: CohortAbstract[];

  branchReconnectPoint: any;

  canDelete = false;
  canDeleteNotice = '';

  isOldVWB = false;

  _editableProperties: string[] = EDITABLE_PROPERTIES;
  _dirtyDependencies: string = DIRTY_DEPENDENCIES;

  prepareForEdit() {
    super.prepareForEdit();

    let actionEdges = _sortBy(
      this.action.campaign.edges.filter((edge: any) => edge.from === this.action.id),
      ['index'],
    );

    let branchReconnectPoint = this.action.campaign.branchRejoinPointsMap[this.action.id];

    let canDelete = true;
    let canDeleteNotice = null;

    if (this.isOldVWB) {
      let { traversedNodes, leafNodes } = retrieveOutgoingEdges(this.action, {
        edges: this.action.campaign.edges,
        reconnectNodeId: branchReconnectPoint,
      });
      // Traversed nodes includes the branch node we're traversing, so we're
      // checking for length of 1
      let isBranchEmpty = traversedNodes.length === 1 && leafNodes.length === 0;

      if (!isBranchEmpty) {
        canDelete = false;

        // Traversed nodes also include the leaf nodes.
        // If traversed nodes is 3 (this branch plus two exits), and there are two
        // exits, let's prompt to reconnect the exits before deleting.
        // Ideally we should be able to handle deleting a branch that only has
        // exits, but that requires jumping through extra hoops, so let's go with
        // this for now.
        if (traversedNodes.length === 3 && leafNodes.length === 2) {
          canDeleteNotice = 'Please reconnect all exits before deleting';
        } else {
          canDeleteNotice = 'Please remove all actions from within this branch before deleting';
        }
      }
    } else {
      let treeNode = this.action.campaign.branchesMap[this.action.id];
      canDelete = edgesEndingInExitCount(treeNode) !== treeNode.edges.length;

      if (!canDelete) {
        // If all edges of this branch action end in exts, prompt to reconnect the
        // exits before deleting.
        // Ideally we should be able to handle deleting a branch that only has
        // exits, but that requires jumping through extra hoops, so let's go with
        // this for now.
        canDeleteNotice = 'Please reconnect all exits before deleting';
      }
    }

    this.setProperties({
      originalEdges: actionEdges,
      cohortAbstracts: this.action.cohorts.map((cohort: number, index: number) => {
        return new CohortAbstract(cohort, actionEdges[index], branchReconnectPoint, this);
      }),
      branchReconnectPoint,
      canDelete,
      canDeleteNotice,
    });
  }

  prepareForSave() {
    let action: any = super.prepareForSave();

    action.set('cohorts', [...this.cohortAbstracts.map((cohortAbstract) => cohortAbstract.value)]);

    return action;
  }

  @computed('originalEdges.[]', 'cohortAbstracts.@each.isDirty') // @ts-ignore override warning for "'_isDirty' is defined as a property in parent class but is overridden here in 'RandomCohortSplitAbstract' as an accessor."
  get _isDirty(): boolean {
    return (
      this.originalEdges.length !== this.cohortAbstracts.length || this.cohortAbstracts.some((cohort) => cohort.isDirty)
    );
  }

  @computed('isDistributedEvenly', 'cohortAbstracts.@each.value')
  get totalCohortAllocations(): number {
    if (this.isDistributedEvenly) {
      return MAX_RANDOM_COHORT_ALLOCATION;
    }

    return this.cohortAbstracts.reduce((acc, cohortAbstract) => {
      return acc + cohortAbstract.value;
    }, 0);
  }

  @computed('totalCohortAllocations')
  get formattedTotalCohortAllocations(): string {
    return formatRandomCohortPathValue(this.totalCohortAllocations, true);
  }

  @computed('cohortAbstracts.@each.value')
  get isDistributedEvenly(): boolean {
    let isAutomaticDistribution = this.cohortAbstracts.every((ca) => ca.value === 1 || ca.value === 0);

    if (isAutomaticDistribution) {
      return true;
    }

    let isDistributedEvenlyManually = this.cohortAbstracts.every((ca) => ca.value === this.cohortAbstracts[0].value);
    let sumsToMax =
      this.cohortAbstracts.reduce((acc, ca) => {
        return acc + ca.value;
      }, 0) === MAX_RANDOM_COHORT_ALLOCATION;

    return isDistributedEvenlyManually && sumsToMax;
  }

  @computed('cohortAbstracts.@each.value', 'isDistributedEvenly')
  get evenDistributionValue(): number {
    if (!this.isDistributedEvenly) {
      return 0;
    }

    let isDistributedEvenlyManually = this.cohortAbstracts.every(
      (cohort) => cohort.value !== 1 && cohort.value === this.cohortAbstracts[0].value,
    );

    if (isDistributedEvenlyManually) {
      return this.cohortAbstracts[0].value;
    }

    let distributedPathCount = this.cohortAbstracts.filter((cohort) => cohort.value === 1).length;
    return Math.floor(MAX_RANDOM_COHORT_ALLOCATION / distributedPathCount);
  }

  saveTask = task({ drop: true }, async () => {
    let action = this.prepareForSave();

    let branchEdges: any[] = [];
    let otherEdges: any[] = [];
    this.action.campaign.consolidatedTree.edges.forEach((edge: any) => {
      if (edge.from === action.id) {
        branchEdges.push(edge);
      } else {
        otherEdges.push(edge);
      }
    });

    branchEdges = branchEdges.sort((a, b) => (a.index > b.index ? 1 : -1));
    let lastEdgeTarget = branchEdges[branchEdges.length - 1].to;

    let hasNoReconnectPoint = this.cohortAbstracts.some((cohort) => cohort.to === -1);

    // If this branch's paths all end in individual exits (no reconnect point
    // available for the branch), we first connect all new paths to the first
    // action of the last currently existing path and save that to the API. Then
    // we issue another call to add individual exits for each of the new paths
    // and reconnect the edges to those.
    let newEdges = this.cohortAbstracts.map((cohort: CohortAbstract, index: number) => ({
      from: cohort.from,
      to: cohort.to === -1 ? lastEdgeTarget : cohort.to,
      type: cohort.type,
      index,
    }));

    try {
      let response = await this.action.campaign.saveAction({ action, edges: [...otherEdges, ...newEdges] });

      if (hasNoReconnectPoint) {
        newEdges = [];
        let exitActions: any[] = [];
        this.cohortAbstracts.forEach((cohort, index) => {
          let { to } = cohort;
          if (cohort.to === -1) {
            to = `${-(exitActions.length + 1)}`;
            exitActions.push({
              id: to,
              type: 'exit_action',
            });
          }

          newEdges.push({
            from: cohort.from,
            to,
            type: cohort.type,
            index,
          });
        });

        await this.action.campaign.addActions({
          actions: exitActions,
          edges: [...otherEdges, ...newEdges],
          copyFromEnv: false,
        });
      }

      this.prepareForEdit();
      return response;
    } catch (e) {
      action.rollbackAttributes();
      throw e;
    }
  });

  deleteTask = task({ drop: true }, async () => {
    return this.action.campaign.removeActionFromWorkflow(this.action);
  });

  addPath() {
    let edge = {
      from: this.action.id,
      to: -1,
      type: 'branch',
      index: null,
    };

    if (!isNone(this.branchReconnectPoint)) {
      edge.to = this.branchReconnectPoint;
    }

    this.cohortAbstracts.pushObject(new CohortAbstract(0, edge, this.branchReconnectPoint, this, true));
  }

  removePath(cohortAbstract: any) {
    let newCohortAbstracts = this.cohortAbstracts.filter((ca) => ca !== cohortAbstract);

    // If only 2 paths are left we need to adjust one of them so that they both
    // add up to 100, because in the 2 path scenario we automatically adjust one
    // slider as the other moves.
    if (newCohortAbstracts.length === 2 && !this.isDistributedEvenly) {
      let firstCohortValue = newCohortAbstracts[0].normalizedValue;
      set(newCohortAbstracts[0], 'value', firstCohortValue);
      set(newCohortAbstracts[1], 'value', MAX_RANDOM_COHORT_ALLOCATION - firstCohortValue);
    }

    if (this.isDistributedEvenly) {
      let isDistributedEvenlyManually = this.cohortAbstracts.every(
        (ca) => ca.value !== 1 && ca.value === this.cohortAbstracts[0].value,
      );

      if (isDistributedEvenlyManually) {
        newCohortAbstracts.forEach((ca) => set(ca, 'value', 1));
      }
    }

    set(this, 'cohortAbstracts', newCohortAbstracts);
  }

  @computed('totalCohortAllocations')
  get errors() {
    if (this.totalCohortAllocations !== MAX_RANDOM_COHORT_ALLOCATION) {
      return {
        cohorts: ['Path allocations need to add up to 100%'],
      };
    }

    return;
  }
}
